import { computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import { ElementalTypeEnum } from '../types/elemental'
import type { Elemental, ElementalFormData, ElementalTypeRoute } from '../types/elemental'
import { useElementalFormStore } from './elementalForm'
import { useLeftSidebarStore } from './leftSidebar'

// left sidebar: 280px
// + main scroll = 370px
// + right sidebar: 320px
export const SAFE_MIN_WINDOW_WIDTH_TWO_SIDEBARS = 970
export type SideBarMode =
  | 'view'
  | 'edit'
  | 'folder'
  | 'export'
  | 'prompt-injector'
  | 'webhooks'
  | 'webhooks-create'
  | 'workflows'
  | 'workflows-create'
  | 'workflows-preview'
  | 'workflows-request-details'

interface GoToParams {
  elemental?: Elemental
  mode: SideBarMode
  webhookId?: string | null
  workflowId?: string | null
  requestId?: string | null
}

interface listOpenData {
  parentId: string | null
  id: string
}

export interface SidebarRightState {
  isOpen: boolean
  mainListOpen: string[]
  history: any[]
  elementalOpen: listOpenData[]
  mode: SideBarMode
  scrollPosition: number
  currentElemental: {
    id: string
    rowId?: string
    colId?: string
  } | null
  webhookId?: string | null
  workflowId?: string | null
  requestId?: string | null
}

const initialState: SidebarRightState = {
  isOpen: false,
  mainListOpen: [],
  history: [],
  elementalOpen: [],
  mode: 'view',
  currentElemental: null,
  scrollPosition: 0,
  webhookId: undefined,
  workflowId: undefined,
  requestId: undefined,
}

const getFromLocalStorage = (): SidebarRightState => {
  try {
    const data = window.localStorage?.getItem('sidebar-right')
    if (!data)
      return reactive(initialState)

    const parsed = JSON.parse(data) as SidebarRightState
    return reactive({
      ...initialState,
      ...parsed,
    })
  }
  catch (error) {
    return reactive({} as SidebarRightState)
  }
}

export const useSidebarStore = defineStore('sidebar', () => {
  const state: SidebarRightState = getFromLocalStorage()

  const toggleSidebar = (value?: boolean) => {
    state.isOpen = value !== undefined ? value : !state.isOpen

    if (state.isOpen && window.innerWidth < SAFE_MIN_WINDOW_WIDTH_TWO_SIDEBARS)
      useLeftSidebarStore().isOpen = false
  }

  const closeTableCellEdit = (closeSidebar?: boolean) => {
    if (closeSidebar)
      state.isOpen = false
    state.mode = 'view'
  }

  const toggleElementOpen = (
    id: string,
    parentId: string | null,
    open: boolean,
  ) => {
    const isOpend = state.elementalOpen.find((list) => {
      return list.id === id && list.parentId === parentId
    })
    if (open) {
      if (!isOpend) {
        state.elementalOpen.push({
          id,
          parentId,
        })
      }
      return
    }
    state.elementalOpen = state.elementalOpen.filter(
      list => !(list.id === id && list.parentId === parentId),
    )
  }

  const isElementalOpen = (id: string, parentId: string | null) => {
    return computed(() =>
      state.elementalOpen.some((list) => {
        // eslint-disable-next-line eqeqeq
        return list.id == id && list.parentId == parentId
      }),
    )
  }

  const openMainList = (idLable: ElementalTypeRoute) => {
    const lists = new Set(state.mainListOpen)
    lists.add(idLable)
    state.mainListOpen = Array.from(lists)
  }

  const isMainListOpen = (idLable: ElementalTypeRoute) => {
    return computed(() => state.mainListOpen.includes(idLable))
  }

  const closeMainList = (idLable: ElementalTypeRoute) => {
    const lists = new Set(state.mainListOpen)
    lists.delete(idLable)
    state.mainListOpen = Array.from(lists)
  }

  const goToEdit = (itemInHistory: ElementalFormData | null, clearHistory = false) => {
    if (clearHistory || state.mode !== 'edit')
      state.history = []
    if (itemInHistory)
      state.history.push(itemInHistory)

    state.isOpen = true
    state.mode = 'edit'
  }

  const goToView = (close?: boolean) => {
    state.mode = 'view'
    // state.history = []
    if (close)
      state.isOpen = false
    state.currentElemental = null
  }

  const goToPrevious = (close?: boolean) => {
    const { open: openElementalForm } = useElementalFormStore()
    if (state.history.length > 0) // Remove o atual da history
      state.history.pop()
    const lastItem = state.history.length > 0 ? state.history.pop() : null

    if (lastItem && state.mode === 'edit') {
      state.isOpen = true
      state.currentElemental = null
      openElementalForm('edit', lastItem, lastItem.elementalTypeId, false)
    }
    else if (lastItem && state.mode && (['workflows', 'webhooks'].includes(state.mode.split('-')[0]))) {
      state.isOpen = true
      state.mode = lastItem.mode
      state.webhookId = lastItem.webhookId
      state.workflowId = lastItem.workflowId
      state.requestId = lastItem.requestId
      state.currentElemental = lastItem.elemental || null
    }
    else { goToView(close) }
  }

  const goToFolder = (elemental: Elemental) => {
    state.mode = 'folder'
    state.isOpen = true
    state.currentElemental = {
      ...elemental,
      id: elemental.id,
      elementalTypeId: ElementalTypeEnum.List,
    } as any
  }

  const goToPromptInjector = (elemental: Elemental) => {
    state.mode = 'prompt-injector'
    state.isOpen = true
    state.currentElemental = {
      ...elemental,
      id: elemental.id,
    } as any
  }

  const goTo = (params: GoToParams) => {
    const { elemental, mode, webhookId, workflowId, requestId } = params
    if (state.mode && mode && state.mode.split('-')[0] !== mode.split('-')[0])
      state.history = []

    state.history.push(params)

    state.mode = mode
    state.isOpen = true
    state.webhookId = webhookId
    state.workflowId = workflowId
    state.requestId = requestId
    if (elemental)
      state.currentElemental = elemental
  }
  const goToExport = (elemental: Elemental) => {
    state.mode = 'export'
    state.isOpen = true
    state.currentElemental = {
      ...elemental,
      id: elemental.id,
      elementalTypeId: ElementalTypeEnum.List,
    } as any
  }

  const updateScrollPosition = (scrollPosition: number) => {
    state.scrollPosition = scrollPosition
  }

  return {
    state,
    toggleSidebar,
    openMainList,
    closeMainList,
    toggleElementOpen,
    isMainListOpen,
    goToEdit,
    goToView,
    goToFolder,
    goToExport,
    closeTableCellEdit,
    isElementalOpen,
    updateScrollPosition,
    goToPromptInjector,
    goToPrevious,
    goTo,
  }
})

useSidebarStore().$subscribe((_, state) => {
  if (state.state.mode === 'view' && useElementalFormStore().isOpen)
    useElementalFormStore().close()

  window.localStorage.setItem('sidebar-right', JSON.stringify(state.state))
})
